<template>
    <div class="min-h-screen">
        <!-- Header Section -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            <!-- Main Content -->
            <div class="bg-white rounded-xl shadow-sm border my-6 border-gray-200">
                <!-- Form Header -->
                <div class="px-6 py-4 border-b border-gray-200">
                    <h2 class="text-lg font-semibold text-gray-900">
                        Generate Report
                    </h2>
                    <p class="text-sm text-gray-600">
                        Select your report type and configure the parameters
                    </p>
                </div>

                <!-- Form Content -->
                <div class="p-6">
                    <!-- Report Form -->
                    <div
                        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        <!-- Enhanced Report Type Selection -->
                        <div class="space-y-3 col-span-full">
                            <label
                                for="reportOn"
                                class="block text-sm font-medium text-gray-700"
                            >
                                <span class="flex items-center">
                                    <svg
                                        class="w-4 h-4 text-gray-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                        ></path>
                                    </svg>
                                    Report Type
                                    <span class="ml-2 text-xs text-gray-500"
                                        >(Required)</span
                                    >
                                </span>
                            </label>

                            <!-- Enhanced Select with Better Styling -->
                            <div class="relative">
                                <select
                                    @change="clear()"
                                    class="block w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                    v-model="reportOn"
                                    name="report_on"
                                    id="reportOn"
                                >
                                    <option
                                        value=""
                                        disabled
                                        selected
                                        class="text-gray-500"
                                    >
                                        Select a report type to generate...
                                    </option>

                                    <!-- Sales & Revenue Reports -->
                                    <optgroup
                                        label="Sales & Revenue Reports"
                                        class="font-semibold text-gray-800"
                                    >
                                        <option
                                            value="branch_sales_report"
                                            class="py-2"
                                        >
                                            Branch Sales Report
                                        </option>
                                        <option
                                            value="agents_sales"
                                            class="py-2"
                                        >
                                            Agents Sales Report
                                        </option>
                                        <option
                                            value="superagents_sales"
                                            class="py-2"
                                        >
                                            Super Agents Report
                                        </option>
                                    </optgroup>

                                    <!-- Financial Reports -->
                                    <optgroup
                                        label="Financial Reports"
                                        class="font-semibold text-gray-800"
                                    >
                                        <!-- <option value="deposits_vs_sales" class="py-2">
                                                Deposits vs Sales Report
                                            </option> -->
                                        <!-- <option
                                            value="balance_report"
                                            class="py-2"
                                        >
                                            Balance Report
                                        </option> -->
                                        <option
                                            value="agent_reconciliation"
                                            class="py-2"
                                        >
                                            Agent Reconciliation Report
                                        </option>
                                        <option
                                            value="financial_summary_report"
                                            class="py-2"
                                        >
                                            Financial Summary Report
                                        </option>
                                        <option value="deposits" class="py-2">
                                            Deposits Report
                                        </option>
                                    </optgroup>

                                    <!-- Operations Reports -->
                                    <optgroup
                                        label="Operations Reports"
                                        class="font-semibold text-gray-800"
                                    >
                                        <option
                                            value="worksheets_report"
                                            class="py-2"
                                        >
                                            Worksheets Report
                                        </option>
                                        <option
                                            value="daily_cash_collection_report"
                                            class="py-2"
                                        >
                                            Daily Cash Collection Report
                                        </option>
                                        <option
                                            value="daily_cash_reconciliation_report"
                                            class="py-2"
                                        >
                                            Daily Cash Reconciliation Report
                                        </option>
                                    </optgroup>

                                    <!-- Management Reports -->
                                    <optgroup
                                        label="Management Reports"
                                        class="font-semibold text-gray-800"
                                    >
                                        <option
                                            value="float_replenishments"
                                            class="py-2"
                                        >
                                            Float Replenishments
                                        </option>
                                        <option
                                            value="account_replenishments"
                                            class="py-2"
                                        >
                                            Account Replenishments
                                        </option>
                                        <option
                                            value="revise_report"
                                            class="py-2"
                                        >
                                            Revise Log Report
                                        </option>
                                        <option
                                            value="denominator_report"
                                            class="py-2"
                                        >
                                            Denominator Report
                                        </option>
                                    </optgroup>

                                    <!-- Utility Reports -->
                                    <optgroup
                                        label="Utility Reports"
                                        class="font-semibold text-gray-800"
                                    >
                                        <option
                                            value="nawec_running_balance"
                                            class="py-2"
                                        >
                                            NAWEC Running Balance
                                        </option>
                                        <option
                                            value="nawec_recovery"
                                            class="py-2"
                                        >
                                            NAWEC Recovery Report
                                        </option>
                                        <option value="sms_report" class="py-2">
                                            SMS Report
                                        </option>
                                        <option value="customer" class="py-2">
                                            Customer Report
                                        </option>
                                    </optgroup>
                                </select>

                                <!-- Custom Dropdown Arrow -->
                                <div
                                    class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                >
                                    <svg
                                        class="w-5 h-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M19 9l-7 7-7-7"
                                        ></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- Date Range Inputs -->
                        <template v-if="reportOn !== 'customer'">
                            <div class="space-y-2">
                                <label
                                    for="from"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    <span class="flex items-center">
                                        <svg
                                            class="w-4 h-4 text-gray-500 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            ></path>
                                        </svg>
                                        From Date
                                    </span>
                                </label>
                                <input
                                    class="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    type="date"
                                    name="from"
                                    v-model="from"
                                    id="from"
                                />
                            </div>
                            <div class="space-y-2">
                                <label
                                    for="to"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    <span class="flex items-center">
                                        <svg
                                            class="w-4 h-4 text-gray-500 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            ></path>
                                        </svg>
                                        To Date
                                    </span>
                                </label>
                                <input
                                    class="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    type="date"
                                    name="to"
                                    v-model="to"
                                    id="to"
                                />
                            </div>
                        </template>

                        <!-- Additional Options based on Report Type -->
                        <div
                            v-if="reportOn == 'denominator_report'"
                            class="space-y-2"
                        >
                            <label
                                for="filterBy"
                                class="block text-sm font-medium text-gray-700"
                            >
                                <span class="flex items-center">
                                    <svg
                                        class="w-4 h-4 text-gray-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                        ></path>
                                    </svg>
                                    Filter By
                                </span>
                            </label>
                            <div class="relative">
                                <select
                                    @change="onBranchSelected()"
                                    class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                    v-model="filter_option"
                                    name="filterBy"
                                    id="filterBy"
                                >
                                    <option
                                        value=""
                                        disabled
                                        selected
                                        class="text-gray-500"
                                    >
                                        Select filter option...
                                    </option>
                                    <option value="branch">Branches</option>
                                    <option value="agent">Agents</option>
                                    <option value="super_agent">
                                        Super Agents
                                    </option>
                                </select>
                                <div
                                    class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                >
                                    <svg
                                        class="w-5 h-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M19 9l-7 7-7-7"
                                        ></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="reportOn == 'financial_summary_report'"
                            class="space-y-2"
                        >
                            <label
                                for="accounts"
                                class="block text-sm font-medium text-gray-700"
                            >
                                <span class="flex items-center">
                                    <svg
                                        class="w-4 h-4 text-gray-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                        ></path>
                                    </svg>
                                    Accounts
                                </span>
                            </label>
                            <div class="relative">
                                <select
                                    class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                    v-model="account"
                                    name="accounts"
                                    id="accounts"
                                >
                                    <option
                                        value=""
                                        disabled
                                        selected
                                        class="text-gray-500"
                                    >
                                        Select an account...
                                    </option>
                                    <option value="all" class="font-medium">
                                        All Accounts
                                    </option>
                                    <option
                                        :value="acc.id"
                                        v-for="(acc, i) in accounts"
                                        :key="i"
                                    >
                                        {{ acc.name }}
                                    </option>
                                </select>
                                <div
                                    class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                >
                                    <svg
                                        class="w-5 h-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M19 9l-7 7-7-7"
                                        ></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- Worksheets Report Options -->
                        <template v-if="reportOn == 'worksheets_report'">
                            <div class="space-y-2">
                                <label
                                    for="banked"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    <span class="flex items-center">
                                        <svg
                                            class="w-4 h-4 text-gray-500 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                            ></path>
                                        </svg>
                                        Banking Status
                                    </span>
                                </label>
                                <div class="relative">
                                    <select
                                        class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                        v-model="isBanked"
                                        name="banked"
                                        id="banked"
                                    >
                                        <option
                                            value=""
                                            disabled
                                            selected
                                            class="text-gray-500"
                                        >
                                            Select banking status...
                                        </option>
                                        <option value="all">All</option>
                                        <option value="1">Banked</option>
                                        <option value="0">Not Banked</option>
                                    </select>
                                    <div
                                        class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                    >
                                        <svg
                                            class="w-5 h-5 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 9l-7 7-7-7"
                                            ></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="shortageStatus"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    <span class="flex items-center">
                                        <svg
                                            class="w-4 h-4 text-gray-500 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                                            ></path>
                                        </svg>
                                        Shortage Status
                                    </span>
                                </label>
                                <div class="relative">
                                    <select
                                        class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                        v-model="isShortage"
                                        name="shortageStatus"
                                        id="shortageStatus"
                                    >
                                        <option
                                            value=""
                                            disabled
                                            selected
                                            class="text-gray-500"
                                        >
                                            Select shortage status...
                                        </option>
                                        <option value="all">All</option>
                                        <option value="yes">
                                            Has Shortage
                                        </option>
                                        <option value="no">No Shortage</option>
                                    </select>
                                    <div
                                        class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                    >
                                        <svg
                                            class="w-5 h-5 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 9l-7 7-7-7"
                                            ></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- Branch and Staff Selection -->
                        <template
                            v-if="
                                reportOn == 'branch_sales' ||
                                reportOn == 'daily_cash_collection_report'
                            "
                        >
                            <div class="space-y-2">
                                <label
                                    for="branch"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    <span class="flex items-center">
                                        <svg
                                            class="w-4 h-4 text-gray-500 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                            ></path>
                                        </svg>
                                        Branch
                                    </span>
                                </label>
                                <div class="relative">
                                    <select
                                        @change="onBranchSelected()"
                                        class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                        v-model="branch"
                                        name="branch"
                                        id="branch"
                                    >
                                        <option
                                            value=""
                                            disabled
                                            selected
                                            class="text-gray-500"
                                        >
                                            Select a branch...
                                        </option>
                                        <option value="all" class="font-medium">
                                            All Branches
                                        </option>
                                        <option
                                            :value="branch.id"
                                            v-for="(branch, i) in branchs"
                                            :key="i"
                                        >
                                            {{ branch.name }}
                                        </option>
                                    </select>
                                    <div
                                        class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                    >
                                        <svg
                                            class="w-5 h-5 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 9l-7 7-7-7"
                                            ></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="staff"
                                    class="block text-sm font-medium text-gray-700"
                                >
                                    <span class="flex items-center">
                                        <svg
                                            class="w-4 h-4 text-gray-500 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            ></path>
                                        </svg>
                                        Staff
                                    </span>
                                </label>
                                <div class="relative">
                                    <select
                                        class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                        v-model="staff"
                                        name="staff"
                                        id="staff"
                                    >
                                        <option
                                            value=""
                                            disabled
                                            selected
                                            class="text-gray-500"
                                        >
                                            Select a staff member...
                                        </option>
                                        <option value="all" class="font-medium">
                                            All Staff
                                        </option>
                                        <option
                                            :value="staff.id"
                                            v-for="(staff, i) in staffs"
                                            :key="i"
                                        >
                                            {{ staff.first_name }}
                                            {{ staff.last_name }}
                                        </option>
                                    </select>
                                    <div
                                        class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                    >
                                        <svg
                                            class="w-5 h-5 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 9l-7 7-7-7"
                                            ></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <div
                            v-if="reportOn == 'superagents_sales'"
                            class="space-y-2"
                        >
                            <label
                                for="superAgent"
                                class="block text-sm font-medium text-gray-700"
                            >
                                <span class="flex items-center">
                                    <svg
                                        class="w-4 h-4 text-gray-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                        ></path>
                                    </svg>
                                    Super Agents
                                </span>
                            </label>
                            <select
                                class="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                v-model="superagent"
                                name="superAgent"
                                id="superAgent"
                            >
                                <option selected disabled>
                                    Select a super agent
                                </option>
                                <option value="all">All Super Agents</option>
                                <option
                                    v-for="(agent, i) in agents.data"
                                    :value="agent.id"
                                    :key="i"
                                >
                                    {{ agent.entity_name }}
                                </option>
                            </select>
                        </div>
                        <!-- Enhanced Agents Selection -->
                        <div
                            v-if="reportOn == 'agents_sales'"
                            class="space-y-0"
                        >
                            <label
                                for="agent"
                                class="block text-sm font-medium text-gray-700 mb-2"
                            >
                                <span class="flex items-center">
                                    <svg
                                        class="w-4 h-4 text-gray-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                        ></path>
                                    </svg>
                                    Select Agents
                                </span>
                            </label>
                            <div class="relative">
                                <select
                                    class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 appearance-none"
                                    v-model="agent"
                                    name="agent"
                                    id="agent"
                                >
                                    <option
                                        value=""
                                        disabled
                                        selected
                                        class="text-gray-500"
                                    >
                                        Choose an agent to analyze...
                                    </option>
                                    <option value="all" class="font-medium">
                                        All Agents
                                    </option>
                                    <optgroup
                                        label="Individual Agents"
                                        class="font-semibold text-gray-800"
                                    >
                                        <option
                                            v-for="(
                                                agent, i
                                            ) in chapmanagents.data"
                                            :value="agent.id"
                                            :key="i"
                                            class="py-1"
                                        >
                                            {{ agent.entity_name }}
                                        </option>
                                    </optgroup>
                                </select>

                                <!-- Custom Dropdown Arrow -->
                                <div
                                    class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                                >
                                    <svg
                                        class="w-5 h-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M19 9l-7 7-7-7"
                                        ></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="showError" class="mt-6">
                        <div
                            class="bg-red-50 border border-red-200 rounded-lg p-4"
                        >
                            <div class="flex">
                                <svg
                                    class="w-5 h-5 text-red-400 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    ></path>
                                </svg>
                                <p class="text-sm text-red-700">
                                    Please check your input and try again
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Generate Button Section -->
                    <div class="mt-8 pt-6 px-4 border-t border-gray-200">
                        <div class="flex items-center justify-between">
                            <div class="text-sm text-gray-600">
                                <span
                                    v-if="!validation"
                                    class="flex items-center"
                                >
                                    <svg
                                        class="w-4 h-4 text-amber-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                                        ></path>
                                    </svg>
                                    Please fill in all required fields
                                </span>
                                <span
                                    v-else
                                    class="flex items-center text-green-600"
                                >
                                    <svg
                                        class="w-4 h-4 text-green-500 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        ></path>
                                    </svg>
                                    Ready to generate report
                                </span>
                            </div>
                            <button
                                :disabled="!validation || isLoading"
                                @click="generate()"
                                class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                                <svg
                                    v-if="isLoading"
                                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    ></circle>
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                <svg
                                    v-else
                                    class="w-5 h-5 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                    ></path>
                                </svg>
                                <span v-if="!isLoading">Generate Report</span>
                                <span v-else>Generating...</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import SuperAgents from '../../mixins/SuperAgents';
import Agents from '../../mixins/Agents';

export default {
    middleware: 'auth',
    layout: 'default',
    mixins: [SuperAgents, Agents],

    data: () => ({
        branchs: null,
        accounts: [],
        account: null,
        staffs: null,
        isLoading: false,
        staff: 'all',
        isBanked: 'all',
        isShortage: 'all',
        superagent: 'all',
        agent: 'all',
        branch: 'all',
        reportOn: '',
        reportBy: '',
        filter_option_id: '',
        filter_option: '',
        from: '',
        to: '',
        showError: false,
    }),

    computed: {
        validation: function () {
            // console.log('report on', this.reportOn);
            if (this.from == '' || this.to == '' || this.reportOn == '') {
                if (this.reportOn == 'customer') {
                    return true;
                }
                return false;
            }

            return true;
        },
    },

    mounted() {
        this.loadAccounts();
    },

    created() {
        var _this = this;
        _this.loadBranches();
    },

    methods: {
        getReportDescription(reportType) {
            const descriptions = {
                deposits_vs_sales: 'Deposits vs Sales Report',
                branch_sales_report: 'Branch Sales Analysis',
                agents_sales: 'Agent Performance Report',
                superagents_sales: 'Super Agent Commission Report',
                balance_report: 'Account Balance Overview',
                agent_reconciliation: 'Agent Reconciliation Report',
                financial_summary_report: 'Complete Financial Summary',
                deposits: 'Deposit Transaction Analysis',
                worksheets_report: 'Daily Worksheet Tracking',
                daily_cash_collection_report: 'Daily Cash Flow Report',
                daily_cash_reconciliation_report: 'Cash Reconciliation Report',
                float_replenishments: 'Float Management Report',
                account_replenishments: 'Account Replenishment Report',
                revise_report: 'System Revision Log',
                denominator_report: 'Currency Denomination Report',
                nawec_running_balance: 'NAWEC Utility Balance',
                nawec_recovery: 'NAWEC Recovery Report',
                sms_report: 'SMS Delivery Report',
                customer: 'Customer Data Export',
            };
            return descriptions[reportType] || 'Report Description';
        },

        getReportDetails(reportType) {
            const details = {
                deposits_vs_sales:
                    'Comprehensive summary of all agent activities including deposits, sales, commissions (75% agent, 25% Chapman), and reversals. Shows net activity and performance metrics for all agents in the system.',
                agent_reconciliation:
                    'Compare agent actual balances against expected balances based on deposits, sales, and commissions. Identify discrepancies that may indicate accounting issues or unpaid commissions.',
                branch_sales_report:
                    'Comprehensive analysis of sales performance across all branches with detailed metrics and comparisons.',
                agents_sales:
                    'Individual agent performance tracking including sales volume, commission earned, and transaction counts.',
                superagents_sales:
                    'Detailed commission breakdown and transaction summary for super agents with filtering options.',
                balance_report:
                    'Current account balances and financial position overview for all accounts and branches.',
                financial_summary_report:
                    'Complete financial overview including revenue, expenses, and profit analysis.',
                deposits:
                    'Detailed analysis of all deposit transactions including amounts, dates, and processing status.',
                worksheets_report:
                    'Daily worksheet tracking with banking status and shortage analysis capabilities.',
                daily_cash_collection_report:
                    'Daily cash flow tracking and collection analysis by branch and staff.',
                daily_cash_reconciliation_report:
                    'Daily cash reconciliation with discrepancy identification and resolution tracking.',
                float_replenishments:
                    'Float management and replenishment tracking for operational cash flow.',
                account_replenishments:
                    'Account refill tracking and management for various service accounts.',
                revise_report:
                    'System revision history and audit trail for all modifications and updates.',
                denominator_report:
                    'Currency denomination analysis and cash management reporting.',
                nawec_running_balance:
                    'NAWEC utility account balance tracking and transaction history.',
                nawec_recovery:
                    'Track recovered NAWEC transactions with detailed statistics, user breakdown, and daily analysis.',
                sms_report:
                    'SMS delivery status and messaging analytics for system notifications.',
                customer:
                    'Complete customer database export with all relevant customer information.',
            };
            return (
                details[reportType] ||
                'Detailed report information will be displayed here.'
            );
        },

        clear() {
            this.staffs = null;
            this.branch = 'all';
        },

        async generate() {
            this.isLoading = true;
            console.log('staffs///', this.staff);
            console.log('reportOn///', this.reportOn);

            // Special handling for Deposits vs Sales report
            if (this.reportOn === 'deposits_vs_sales') {
                this.$router.push({
                    name: 'DepositsVsSalesReport',
                    query: {
                        from: this.from,
                        to: this.to,
                    },
                });
                this.isLoading = false;
                return true;
            }

            // Special handling for Agent Reconciliation report
            if (this.reportOn === 'agent_reconciliation') {
                this.$router.push({
                    name: 'AgentReconciliationReport',
                    query: {
                        date: this.from,
                    },
                });
                this.isLoading = false;
                return true;
            }

            try {
                const { data } = await axios.get(
                    `/report/generate?from=${this.from}&to=${this.to}&superagent=${this.superagent}&agent=${this.agent}&filter_option=${this.filter_option}&filter_option_id=${this.filter_option_id}&report=${this.reportOn}&reportBy=${this.reportBy}&branch=${this.branch}&staff=${this.staff}&account_id=${this.account}&isShortage=${this.isShortage}&isBanked=${this.isBanked} `
                );

                console.log('Report data', data);
                this.$router.push({
                    name: 'generateReportResult',
                    params: {
                        result: data,
                        to: this.to,
                        from: this.from,
                        reportOn: this.reportOn,
                        reportBy: this.reportBy,
                        branch: this.branch,
                        staff: this.staff,
                        isShortage: this.isShortage,
                        isBanked: this.isBanked,
                        agent: this.agent,
                        superagent: this.superagent,
                    },
                });

                this.report = data;
                this.isLoading = false;

                return true;
            } catch (e) {
                console.log('Error', e);
                this.isLoading = false;

                return false;
            }
        },

        async loadAccounts() {
            try {
                const { data } = await axios.get('/accounts');
                this.accounts = data.data;
            } catch (error) {
                console.log('Error', error);
            }
        },

        async loadBranches() {
            // Submit the form

            try {
                const { data } = await axios.get(`/get/branches`);

                this.branchs = data.data;

                // this.isLoading = false;

                return true;
            } catch (e) {
                console.log('Error');
                this.isLoading = false;
                return false;
            }
        },

        onBranchSelected() {
            this.loadStaffs();
        },

        async loadStaffs() {
            // Submit the form
            // console.log("SATFF", id);
            try {
                const { data } = await axios.get(
                    `/staffs/branch/${this.branch}`
                );
                console.log('staffs', data);
                this.staffs = data.data;
                // this.isLoading = false;

                return true;
            } catch (e) {
                console.log('Error');
                this.isLoading = false;
                return false;
            }
        },
    },
};
</script>
